import React, { useState, useEffect, useContext } from 'react';
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import './gallery.css';
import { LoadingContext } from "../../composants/loadingContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImages, faExpand, faImage, faVideo, faPodcast, faPlayCircle, faHeadphones, faClock, faEye, faCalendar } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import CustomAudioPlayer from './CustomAudioPlayer';
import CustomVideoPlayer from './CustomVideoPlayer';

const Gallery = () => {
    const { loading, setLoading } = useContext(LoadingContext);
    const [activeTab, setActiveTab] = useState('photos'); // photos, videos, podcasts

    const [audios, setAudios] = useState([]);
    const [videos, setVideos] = useState([]);
    const [images, setImages] = useState([]);

    const fetchFiles = async () => {
        try {
            const response = await axiosInstance().get('public/gallery/active');
            const data = response.data;
            const imagesData = [];
            const videosData = [];
            const audiosData = [];
            if (data) {
                for (const element of data) {
                    if (element.type === 'IMAGE') {
                        imagesData.push({
                            original: element.path,
                            thumbnail: element.path,
                            originalTitle: element.title,
                            originalAlt: element.title,
                            description: (
                                <div className="custom-gallery-desc">
                                    <div className="bg-black/60 backdrop-blur-md p-4 md:p-6 mb-4 rounded-2xl max-w-2xl mx-auto border border-white/10 shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                                        <h4 className="text-xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-indigo-300 mb-2 drop-shadow-md">
                                            {element.title}
                                        </h4>
                                        <p className="text-gray-200 text-sm md:text-base font-medium leading-relaxed drop-shadow-sm m-0">
                                            {element.description}
                                        </p>
                                    </div>
                                </div>
                            )
                        });
                    } else if (element.type === 'VIDEO') {
                        videosData.push(
                            {
                                id: element.id,
                                title: element.title,
                                description: element.description,
                                duration: element.duration,
                                date: element.date,
                                path: element.path,
                                views: element.views,
                                bg: element.path
                            }
                        );
                    } else if (element.type === 'AUDIO') {
                        audiosData.push(
                            {
                                id: element.id,
                                title: element.title,
                                description: element.description,
                                duration: element.duration,
                                date: element.date,
                                path: element.path
                            }
                        );
                    }
                }
                setImages(imagesData);
                setVideos(videosData);
                setAudios(audiosData);
            } else {
                setImages([]);
                setVideos([]);
                setAudios([]);
            }
        } catch (error) {
            setImages([]);
            setVideos([]);
            setAudios([]);
        }
    };

    useEffect(() => {
        document.title = 'Médiathèque - YouthFP';
        fetchFiles();
    }, []);




    return (
        <>
            <div className={`relative transition-all duration-700 text-white py-20 overflow-hidden ${activeTab === 'photos' ? 'bg-gradient-to-br from-pink-600 via-purple-500 to-indigo-600' : activeTab === 'videos' ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-sky-500' : 'bg-gradient-to-br from-teal-600 via-emerald-500 to-cyan-500'}`}>
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6 shadow-2xl transform transition-transform hover:scale-105">
                            {activeTab === 'photos' && <FontAwesomeIcon icon={faImages} className="text-4xl" />}
                            {activeTab === 'videos' && <FontAwesomeIcon icon={faPlayCircle} className="text-4xl" />}
                            {activeTab === 'podcasts' && <FontAwesomeIcon icon={faHeadphones} className="text-4xl" />}
                        </div>
                        <h1 className="text-5xl md:text-6xl font-extrabold mb-4 tracking-tight drop-shadow-md">
                            Médiathèque
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-6">
                            Découvrez nos contenus en photos, vidéos et podcasts
                        </p>
                    </div>
                </div>
            </div>

            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-12">
                {/* Onglets */}
                <div className="flex justify-center -mt-8 relative z-20 mb-12 px-4">
                    <div className="bg-white p-2 rounded-2xl shadow-xl flex gap-2 flex-wrap justify-center border border-gray-100">
                        <button
                            onClick={() => setActiveTab('photos')}
                            className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border-none outline-none focus:outline-none ${activeTab === 'photos' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md' : 'bg-transparent text-gray-600 hover:bg-gray-100'}`}
                        >
                            <FontAwesomeIcon icon={faImages} /> Galerie Photos
                        </button>
                        <button
                            onClick={() => setActiveTab('videos')}
                            className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border-none outline-none focus:outline-none ${activeTab === 'videos' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' : 'bg-transparent text-gray-600 hover:bg-gray-100'}`}
                        >
                            <FontAwesomeIcon icon={faPlayCircle} /> Vidéos
                        </button>
                        <button
                            onClick={() => setActiveTab('podcasts')}
                            className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border-none outline-none focus:outline-none ${activeTab === 'podcasts' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-md' : 'bg-transparent text-gray-600 hover:bg-gray-100'}`}
                        >
                            <FontAwesomeIcon icon={faHeadphones} /> Podcasts
                        </button>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Vue PHOTOS */}
                    {activeTab === 'photos' && (
                        <div className="animate-fadeIn">
                            <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 border-l-4 border-l-purple-500">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-inner">
                                        <FontAwesomeIcon icon={faExpand} className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800 mb-1">
                                            Mode Plein Écran
                                        </h3>
                                        <p className="text-gray-600 text-sm m-0">
                                            Cliquez sur l'icône plein écran de la galerie pour une expérience immersive en haute définition.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {images && images.length > 0 ? (
                                <div className="bg-black rounded-3xl shadow-2xl overflow-hidden border-4 border-white p-2">
                                    <ImageGallery
                                        showNav={true}
                                        showFullscreenButton={true}
                                        showPlayButton={true}
                                        thumbnailPosition={"bottom"}
                                        items={images}
                                        slideInterval={3000}
                                        slideDuration={450}
                                    />
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
                                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-50 rounded-full mb-6 border border-gray-200">
                                        <FontAwesomeIcon icon={faImages} className="text-5xl text-gray-300" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Aucune photo n'est disponible</h3>
                                    <p className="text-gray-500 text-lg max-w-md mx-auto">
                                        La galerie photo de YouthFP sera bientôt mise à jour avec les images de nos activités sur le terrain. Revenez plus tard !
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Vue VIDÉOS */}
                    {activeTab === 'videos' && (
                        <div className="animate-fadeIn">
                            {videos && videos.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {videos.map((video) => (
                                        <div key={video.id} className="relative group bg-white rounded-3xl p-1 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden flex flex-col">
                                            {/* Glow border gradient effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-indigo-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl z-0"></div>
                                            
                                            <div className="bg-white rounded-[22px] h-full p-4 md:p-6 relative z-10 flex flex-col flex-grow">
                                                
                                                {/* Video Player Section Dynamique */}
                                                <div className="mb-5 relative group/player">
                                                    <CustomVideoPlayer src={video.path} poster={video.bg} />
                                                </div>

                                                <div className="flex flex-col sm:flex-row gap-4 items-start flex-1">
                                                    {/* Custom Icon */}
                                                    <div className="hidden sm:flex relative w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-blue-500/30 transition-transform duration-300 transform group-hover:scale-105">
                                                        <FontAwesomeIcon icon={faVideo} className="text-xl text-white z-10" />
                                                    </div>
                                                    
                                                    {/* Title & Meta */}
                                                    <div className="flex-1 w-full flex flex-col h-full">
                                                        <h3 className="text-xl font-extrabold text-gray-800 line-clamp-2 leading-tight mb-2 group-hover:text-blue-600 transition-colors" title={video.title}>
                                                            {video.title}
                                                        </h3>
                                                        
                                                        {video.description && (
                                                            <p className="text-gray-500 text-sm line-clamp-2 mb-3 leading-relaxed flex-grow">
                                                                {video.description}
                                                            </p>
                                                        )}
                                                        
                                                        <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-gray-500 mt-auto pt-2 border-t border-gray-100/50">
                                                            {video.date && (
                                                                <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-100">
                                                                    <FontAwesomeIcon icon={faCalendar} /> 
                                                                    {new Date(video.date).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                            {video.duration && (
                                                                <span className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full border border-indigo-100">
                                                                    <FontAwesomeIcon icon={faClock} /> 
                                                                    {video.duration}
                                                                </span>
                                                            )}
                                                            {video.views !== undefined && (
                                                                <span className="flex items-center gap-1.5 bg-gray-50 text-gray-700 px-3 py-1 rounded-full border border-gray-100">
                                                                    <FontAwesomeIcon icon={faEye} /> 
                                                                    {video.views} vues
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-white/40 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-12 md:p-20 text-center border border-white/50 relative overflow-hidden">
                                    <div className="absolute -top-24 -left-24 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                                    <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
                                    
                                    <div className="relative z-10">
                                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl mb-8 shadow-lg transform hover:rotate-12 transition-transform duration-500">
                                            <FontAwesomeIcon icon={faVideo} className="text-5xl text-white" />
                                        </div>
                                        <h2 className="text-4xl md:text-6xl font-black text-gray-800 mb-6 tracking-tight">
                                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Coming Soon</span>
                                        </h2>
                                        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
                                            Notre catalogue de reportages et interviews vidéo est en cours de préparation. 
                                            Restez connectés pour découvrir nos actions sur le terrain très prochainement.
                                        </p>
                                        <div className="flex justify-center gap-3">
                                            <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce delay-100"></div>
                                            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce delay-200"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Vue PODCASTS */}
                    {activeTab === 'podcasts' && (
                        <div className="animate-fadeIn">
                            {audios && audios.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {audios.map((audio) => (
                                        <div key={audio.id} className="relative group bg-white rounded-3xl p-1 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
                                            {/* Glow border gradient effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-teal-400 via-emerald-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl z-0"></div>
                                            
                                            <div className="bg-white rounded-[22px] h-full p-6 relative z-10 flex flex-col justify-between">
                                                <div className="flex flex-col sm:flex-row gap-5 mb-5 items-start">
                                                    {/* Cover Icon placeholder */}
                                                    <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-teal-500/30 transition-all duration-300 transform group-hover:scale-105">
                                                        <FontAwesomeIcon icon={faPodcast} className="text-3xl text-white z-10" />
                                                        <div className="absolute -bottom-3 -right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,0,0,0.1)]">
                                                            <FontAwesomeIcon icon={faPlayCircle} className="text-teal-600 text-2xl" />
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Title & Meta */}
                                                    <div className="flex-1 w-full">
                                                        <h3 className="text-xl font-extrabold text-gray-800 line-clamp-2 leading-tight mb-2 group-hover:text-teal-600 transition-colors" title={audio.title}>
                                                            {audio.title}
                                                        </h3>
                                                        
                                                        {audio.description && (
                                                            <p className="text-gray-500 text-sm line-clamp-2 mb-3 leading-relaxed">
                                                                {audio.description}
                                                            </p>
                                                        )}
                                                        
                                                        <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-gray-500">
                                                            {audio.date && (
                                                                <span className="flex items-center gap-1.5 bg-teal-50 text-teal-700 px-3 py-1 rounded-full border border-teal-100">
                                                                    <FontAwesomeIcon icon={faCalendar} /> 
                                                                    {new Date(audio.date).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                            {audio.duration && (
                                                                <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full border border-emerald-100">
                                                                    <FontAwesomeIcon icon={faClock} /> 
                                                                    {audio.duration}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Le lecteur audio dynamique */}
                                                <div className="mt-auto pt-4 border-t border-gray-100/80">
                                                    <CustomAudioPlayer src={audio.path} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-white/40 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-12 md:p-20 text-center border border-white/50 relative overflow-hidden">
                                    <div className="absolute -top-24 -left-24 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl animate-pulse"></div>
                                    <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
                                    
                                    <div className="relative z-10">
                                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl mb-8 shadow-lg transform hover:-rotate-12 transition-transform duration-500">
                                            <FontAwesomeIcon icon={faHeadphones} className="text-5xl text-white" />
                                        </div>
                                        <h2 className="text-4xl md:text-6xl font-black text-gray-800 mb-6 tracking-tight">
                                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Coming Soon</span>
                                        </h2>
                                        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
                                            "La Voix de YouthFP" arrive bientôt ! Préparez vos écouteurs pour nos premiers podcasts 
                                            sur la santé de reproduction et l'engagement des jeunes.
                                        </p>
                                        <div className="flex justify-center gap-3">
                                            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-bounce"></div>
                                            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce delay-100"></div>
                                            <div className="w-3 h-3 bg-emerald-600 rounded-full animate-bounce delay-200"></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default Gallery;
